/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 6/2/2023
 * for userx-support
 */

import React, {useEffect, useState, useMemo} from 'react';
import supportAPI from "../_apis/supportAPI";
import {formatCurrency, formatDate} from "../_utilities/formattingUtils";
import {generateInvoiceSummary} from "../_apis/openChatAPI";
import {forEach} from "react-bootstrap/ElementChildren";

// --- Chart/Date helpers (from AgentTimeManager) ---
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, delta) => new Date(d.getFullYear(), d.getMonth() + delta, 1);
const fmtMonth = (d) => d.toLocaleDateString(undefined, {year: "numeric", month: "long"});

const AdminInvoices = () => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState({company: {id: null, name: null}, items: [], summary:''});
    const [totalMonthly, setTotalMonthly] = useState(formatCurrency(0));
    const [currentInvoiceItems, setCurrentInvoiceItems] = useState([]);
    // --- Chart state ---
    const [monthOffset, setMonthOffset] = useState(0); // 0 = current month, -1 = prev, +1 = next
    const [history, setHistory] = useState([]); // [{ label, total, ym }]
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // --- Fetch chart data for previous months ---
    useEffect(() => {
        setLoading(true);
        setError("");
        // Fetch last 6 months (including selected) using monthsAgo semantics:
        // monthOffset = 0 (this month), 1 (last month), 2 (2 months ago), ...
        const fetchHistory = async () => {
            try {
                const months = [];
                for (let i = 5; i >= 0; i--) {
                    const monthsAgo = monthOffset + i; // positive number of months behind
                    const d = addMonths(startOfMonth(new Date()), -monthsAgo);
                    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    let total = 0;
                    try {
                        const resp = await supportAPI.get(`/company/-1/prevmonthtime/${monthsAgo}`);
                        if (Array.isArray(resp.data)) {
                            total = resp.data.reduce((sum, r) => sum + (parseInt(r.time*10) || 0), 0);
                        }
                    } catch (e) {
                        // If error, just leave total as 0
                    }
                    months.push({
                        label: d.toLocaleDateString(undefined, {month: "short"}),
                        total,
                        ym,
                    });
                }
                setHistory(months);
            } catch (e) {
                setError("Failed to load income history");
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [monthOffset]);

    // --- Month navigation ---
    // Prev should move further into the past (increase monthsAgo),
    // Next should move towards the present (decrease monthsAgo, not below 0)
    const goPrev = () => setMonthOffset(o => o + 1);
    const goNext = () => setMonthOffset(o => Math.max(0, o - 1));

    // --- Max for chart scaling ---
    const maxHistory = useMemo(() => Math.max(0, ...history.map(h => h.total)), [history]);

    useEffect(() => {
        // Reset selected company immediately so details hide while switching months
        setSelectedCompany({company: {id: null, name: null}, items: [], summary: ''});

        // Load invoice items for the selected month
        const load = async () => {
            const response = await supportAPI.get(`/company/-1/prevmonthtime/${monthOffset}`);
            console.log("Loaded invoice items for month offset", monthOffset, response.data);
            setCurrentInvoiceItems(response.data || []);
            // Build companies list from invoice items
            const uniqueCompanies = [];
            for(const obj of response.data || []) {
                const {company_id, company} = obj;
                const existingCompany = uniqueCompanies.find(c => c.id === company_id);
                if (!existingCompany) {
                    uniqueCompanies.push({id: company_id, name: company});
                }
            }
            setTotalMonthly(formatCurrency(uniqueCompanies.reduce((acc, comp) => {
                const compTotal = (response.data || [])
                    .filter(i => i.company_id === comp.id)
                    .reduce((sum, item) => sum + parseInt(item.time*10), 0);
                return acc + compTotal;
            }, 0)));
            setCompanies(uniqueCompanies);
        };
        load();
    }, [monthOffset]);

    // --- UI ---
    return (
        <div className="component-container d-flex flex-column h-100">
            <h3>Invoices</h3>
            {/* Bar chart and month navigation */}
            <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={goPrev}>&lt; Prev</button>
                    <span className="fs-4 fw-bold flex-grow-1 text-center">{fmtMonth(addMonths(startOfMonth(new Date()), -monthOffset))}: {totalMonthly}</span>
                    <button className="btn btn-sm btn-outline-secondary" onClick={goNext}>Next &gt;</button>
                </div>
                <div className="d-flex justify-content-center align-items-end" style={{gap: 8, minHeight: 140}}>
                    {history.map((h, index) => {
                        const pct = maxHistory ? Math.round((h.total / maxHistory) * 100) : 0;
                        return (
                            <div key={`chart-${index}`} className="text-center" style={{width: '15%'}}>
                                <div
                                    title={`${h.label}: ${formatCurrency(h.total)}`}
                                    style={{
                                        height:         120,
                                        display:        "flex",
                                        alignItems:     "flex-end",
                                        justifyContent: "stretch",
                                    }}
                                >
                                    <div
                                        style={{
                                            width:        '100%',
                                            height:       Math.max(4, Math.round((pct / 100) * 110)),
                                            background:   "#888",
                                            borderRadius: 4,
                                        }}
                                    />
                                </div>
                                <small className="d-block mt-1">{h.label}</small>
                            </div>
                        );
                    })}
                </div>
                {loading && <div className="text-center mt-2">Loading income history...</div>}
                {error && <div className="text-danger text-center mt-2">{error}</div>}
            </div>
            {/* ...existing invoice UI... */}
            <div className={`row`}>
                <div className={`col-xs-12 col-sm-6 col-md-4`}>
                    <h4>Companies</h4>
                    {companies ? <ul className={`m-3`}>{companies.map(renderCompany)}</ul> : null}
                </div>
                {selectedCompany && selectedCompany.company && selectedCompany.company.id ? (
                    <div className={`col-xs-12 col-sm-6 col-md-8`}>
                        <h4>{selectedCompany.company.name}</h4>
                        <h5>Invoice Details</h5>
                        <p><strong>Summary</strong><br/>{selectedCompany.summary}</p>
                        <div>
                            <strong>Tickets</strong>
                            <ul>
                                {selectedCompany.tickets && selectedCompany.tickets.map(ticket => (
                                    <li key={ticket.ticket_id} className="mb-3">
                                        <div>
                                            <strong>{ticket.title}</strong> <span className="text-muted">(Total: {formatCurrency(ticket.total_time)})</span>
                                        </div>
                                        <ul>
                                            {ticket.items.map(item => (
                                                <li key={item.date}>
                                                    {formatDate(item.date)} - {item.description} - {formatCurrency(item.time * 10)}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <h4>Monthly Total: {getCurrentTotal()}</h4>
                    </div>
                ) : null}
            </div>
        </div>
    );

    function renderCompany(company) {
        return <li key={company.id} className={`mb-2`} data-id={company.id}
                   onClick={companyChangeHandler}>{company.name}</li>
    }
    function renderDetail(detail) {
        return <li
            key={detail.date}>{formatDate(detail.date)} - {detail.description} - {formatCurrency(detail.time*10)}</li>
    }
    async function companyChangeHandler(e) {
        console.log("Company selected:", e.target.dataset.id, currentInvoiceItems.filter(i => i.company_id === e.target.dataset.id));
        const company = companies.find(c => parseInt(c.id) === parseInt(e.target.dataset.id));
        // Get invoice items for this company
        const items = currentInvoiceItems.filter(i => i.company_id === e.target.dataset.id);
        // Group items by ticket_id and sum time per ticket
        const ticketMap = new Map();
        items.forEach(item => {
            if (!ticketMap.has(item.ticket_id)) {
                ticketMap.set(item.ticket_id, {
                    ticket_id: item.ticket_id,
                    title: item.ticket_title,
                    total_time: 0,
                    items: []
                });
            }
            const ticket = ticketMap.get(item.ticket_id);
            ticket.total_time += parseInt(item.time * 10) || 0;
            ticket.items.push(item);
        });
        const tickets = Array.from(ticketMap.values());
        // Generate summary using OpenAI
        const summary = <span className={`text-secondary small`}>Generating summary...</span>;
        setSelectedCompany({company, items, tickets, summary});
        generateInvoiceSummary(items).then(summaryText => {
            setSelectedCompany(prev => ({...prev, summary: summaryText}));
        }).catch(err => {
            console.error("Failed to generate invoice summary:", err);
            setSelectedCompany(prev => ({...prev, summary: "Failed to generate summary."}));
        });
    }
    function getCurrentTotal() {
        // If no company selected, hide/return 0
        if (!selectedCompany || !selectedCompany.company || !selectedCompany.company.id) {
            return formatCurrency(0);
        }
        if (!selectedCompany.items || selectedCompany.items.length === 0) {
            return formatCurrency(0);
        }
        const total = selectedCompany.items.reduce((acc, obj) => acc + parseInt(obj.time*10), 0);
        return formatCurrency(total);
    }
}

export default AdminInvoices;
